import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.locale('it');

export interface OrarioMatriceLike {
  dipendente: string;
  orari: { [giorno: number]: any[] };
}

const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  const bigint = parseInt(h, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

const getLabelPaletteRgb = (label: string): { bg: [number, number, number]; fg: [number, number, number] } => {
  switch (label) {
    case 'Mattina':
    case 'Mattina P.':
      return { bg: hexToRgb('#35abec'), fg: hexToRgb('#ffffff') };
    case 'Pomeriggio':
    case 'Pomeriggio P.':
      return { bg: hexToRgb('#FDD65C'), fg: hexToRgb('#000000') };
    case 'Intermezzo':
    case 'Intermezzo P.':
      return { bg: hexToRgb('#60b964'), fg: hexToRgb('#ffffff') };
    case 'Riposo':
    case 'RIPOSO':
      return { bg: hexToRgb('#ffffff'), fg: hexToRgb('#000000') };
    case 'ASSENZA':
    case 'Assenza':
      return { bg: hexToRgb('#f44336'), fg: hexToRgb('#ffffff') };
    default:
      return { bg: hexToRgb('#bdbdbd'), fg: hexToRgb('#212121') };
  }
};

export function exportMatriceTabPdf(params: {
  negozio: string;
  meseIndex: number; // 0-based
  anno: number;
  mesi: string[];
  matrice: OrarioMatriceLike[];
  dipendentiAttivi?: { [nome: string]: boolean }; // opzionale: mappa attivi
  periodiMaternita?: Record<string, { start: string; end: string }[]>; // per filtrare
  shouldMaskAssenze: boolean;
  isPrivileged: boolean;
  getDisplayTurnoLabel: (t: any) => string;
}): void {
  const { negozio, meseIndex, anno, mesi, matrice, dipendentiAttivi, periodiMaternita, shouldMaskAssenze, isPrivileged, getDisplayTurnoLabel } = params;
  if (!negozio || meseIndex == null || !anno || !mesi?.length) {
    throw new Error('Parametri PDF non validi');
  }
  if (!matrice || matrice.length === 0) {
    throw new Error('Nessun dato da esportare');
  }
  // Richiesta: A4 verticale (2480 x 3508 px) e massimo 3 settimane per foglio
  const PAGE_WIDTH_PX = 2480;
  const PAGE_HEIGHT_PX = 3508;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'px', format: [PAGE_WIDTH_PX, PAGE_HEIGHT_PX] });
  const meseNome = mesi[meseIndex];
  const title = `Matrice Turni - ${negozio} - ${meseNome} ${anno}`;

  const firstOfMonth = dayjs().year(anno).month(meseIndex).date(1);
  const lastOfMonth = firstOfMonth.endOf('month');
  const daysInMonth = firstOfMonth.daysInMonth();
  const daysToMondayFirst = (firstOfMonth.day() + 6) % 7; // 0..6
  const firstWeekStart = firstOfMonth.subtract(daysToMondayFirst, 'day');
  const daysToSundayLast = (7 - lastOfMonth.day()) % 7; // 0..6
  const lastWeekEnd = lastOfMonth.add(daysToSundayLast, 'day');

  const hasAnyTurnInMonth = (riga: OrarioMatriceLike) => {
    for (let d = 1; d <= daysInMonth; d++) {
      const arr = riga.orari[d];
      if (Array.isArray(arr) && arr.length > 0) return true;
    }
    return false;
  };
  const isInMaternita = (nome: string): boolean => {
    if (!periodiMaternita) return false;
    const periodi = periodiMaternita[nome] || [];
    return periodi.some(p => {
      // Se qualsiasi giorno del mese cade nel range maternità: escludi
      const start = dayjs(p.start);
      const end = dayjs(p.end);
      return start.isBefore(lastOfMonth.add(1,'day')) && end.isAfter(firstOfMonth.subtract(1,'day'));
    });
  };
  const matriceFiltrata: OrarioMatriceLike[] = matrice
    .filter(r => (dipendentiAttivi ? dipendentiAttivi[r.dipendente] !== false : true))
    .filter(r => !isInMaternita(r.dipendente))
    .filter(hasAnyTurnInMonth);

  const weekStarts: dayjs.Dayjs[] = [];
  for (let ws = firstWeekStart; ws.isBefore(lastWeekEnd) || ws.isSame(lastWeekEnd, 'day'); ws = ws.add(7, 'day')) {
    weekStarts.push(ws);
  }

  const pageWidth = (doc as any).internal.pageSize.getWidth();
  const margin = 36; // margine generale del documento
  const colGap = 0;
  const singleColWidth = pageWidth - margin * 2;

  const printPageHeader = () => {
    doc.setFontSize(14);
    doc.text(title, margin, margin);
  };
  printPageHeader();

  let curCol = 0;
  let yOffset = margin + 24;
  const blockSpacing = 8;

  // Nuova richiesta: tutte le settimane (4/5/6) in un'unica pagina A4
  const weeksInGroup = weekStarts.length;
  const availableHeight = PAGE_HEIGHT_PX - (margin + 24) - margin; // spazio verticale utilizzabile
  const dipCount = matriceFiltrata.length;
  const estimatedWeekHeaderHeight = 30;
  const spacingBetweenWeeks = 8;
  const extraStatic = weeksInGroup * estimatedWeekHeaderHeight + (weeksInGroup - 1) * spacingBetweenWeeks;
  const heightForRows = availableHeight - extraStatic;
  let targetRowHeight = heightForRows / (Math.max(dipCount, 1) * weeksInGroup);
  let dynamicFont = Math.min(14, Math.max(7, Math.floor(targetRowHeight - 6)));

  weekStarts.forEach((wStart, idxInGroup) => {
  const leftX = margin + curCol * (singleColWidth + colGap);
      doc.setFontSize(11);
      const weekLabelStart = wStart.format('DD MMM');
      const weekLabelEnd = wStart.add(6, 'day').format('DD MMM');
      doc.text(`Settimana ${idxInGroup + 1} (${weekLabelStart} - ${weekLabelEnd})`, leftX, yOffset);

      const header: string[] = ['Dipendente'];
      for (let i = 0; i < 7; i++) {
        const d = wStart.add(i, 'day');
        const label = d.format('DD ddd');
        header.push(label);
      }

      const body = matriceFiltrata.map(riga => {
        const row: any[] = [{ type: 'name', text: riga.dipendente }];
        for (let i = 0; i < 7; i++) {
          const d = wStart.add(i, 'day');
          const isWeekend = d.day() === 0 || d.day() === 6;
          if (d.month() !== meseIndex) {
            row.push({ type: 'out', weekend: isWeekend });
            continue;
          }
            const dayNum = d.date();
            const orari = riga.orari[dayNum] || [];
            if (orari.length === 0) {
              row.push({ type: 'empty', weekend: isWeekend });
            } else if (orari.length === 1) {
              const t = orari[0];
              const labelBase = getDisplayTurnoLabel(t);
              const times = (t.oraPrevInizio && t.oraPrevFine) ? `${t.oraPrevInizio.substring(0,5)}-${t.oraPrevFine.substring(0,5)}` : '';
              const labelShow = (labelBase === 'Assenza')
                ? (shouldMaskAssenze ? labelBase : (isPrivileged ? `${labelBase}${t.codiceTipoOrario ? ` (${t.codiceTipoOrario})` : ''}` : labelBase))
                : labelBase;
              const { bg, fg } = getLabelPaletteRgb(labelBase);
              row.push({ type: 'single', label: labelShow, times, weekend: isWeekend, colorBg: bg, colorFg: fg });
            } else {
              const labels = orari.map(t => {
                const base = getDisplayTurnoLabel(t);
                if (base === 'Assenza') {
                  if (shouldMaskAssenze) return base;
                  return isPrivileged ? `${base}${t.codiceTipoOrario ? ` (${t.codiceTipoOrario})` : ''}` : base;
                }
                return base;
              });
              row.push({ type: 'multi', labels, weekend: isWeekend });
            }
        }
        return row;
      });

      autoTable(doc, {
        head: [header],
        body,
        theme: 'grid',
  startY: yOffset + 12,
        margin: { left: leftX, right: pageWidth - (leftX + singleColWidth) },
        tableWidth: singleColWidth,
    styles: { fontSize: dynamicFont, cellPadding: { top: 6, bottom: 6, left: 2, right: 2 }, overflow: 'linebreak', halign: 'center', valign: 'middle' },
    headStyles: { fillColor: [96, 125, 139], halign: 'center', valign: 'middle', textColor: 255, fontSize: dynamicFont + 1 },
    // Dimezza larghezza colonne giorno: aumentiamo dipendente al 30%, resto suddiviso
    columnStyles: { 0: { cellWidth: Math.min(380, singleColWidth * 0.30), halign: 'left' } },
        didParseCell: (data: any) => {
          if (data.section === 'head') return;
          const colIdx = data.column.index;
          const raw = data.cell.raw;
          if (colIdx === 0) {
            if (raw && typeof raw === 'object') {
              data.cell.text = [raw.text || ''];
            }
            data.cell.styles.halign = 'left';
            data.cell.styles.fillColor = [250, 250, 250];
            return;
          }
          if (raw && typeof raw === 'object') {
            if (raw.type === 'out') {
              data.cell.text = [''];
              data.cell.styles.fillColor = raw.weekend ? [245,245,245] : [255,255,255];
            } else if (raw.type === 'empty') {
              data.cell.text = ['-'];
              data.cell.styles.textColor = [158, 158, 158];
              data.cell.styles.fillColor = raw.weekend ? [245,245,245] : [255,255,255];
            } else if (raw.type === 'single') {
              const lines: string[] = raw.times ? [raw.label, raw.times] : [raw.label];
              data.cell.text = lines;
              data.cell.styles.fillColor = raw.colorBg;
              data.cell.styles.textColor = raw.colorFg;
              data.cell.styles.fontStyle = 'bold';
            } else if (raw.type === 'multi') {
              data.cell.text = raw.labels && raw.labels.length ? raw.labels : [''];
              data.cell.styles.fillColor = raw.weekend ? [245,245,245] : [255,255,255];
              data.cell.styles.textColor = [33,33,33];
            }
          }
        }
      });

      const lastY = (doc as any).lastAutoTable?.finalY || (yOffset + 60);
      yOffset = lastY + blockSpacing;
  });

  const fileName = `Matrice_Turni_${negozio}_${meseNome}_${anno}.pdf`;
  doc.save(fileName);
}

export async function exportMatriceScreenshotPdf(
  container: HTMLElement,
  params: { negozio: string; meseIndex: number; anno: number; mesi: string[] }
): Promise<void> {
  const { negozio, meseIndex, anno, mesi } = params;
  if (!container) {
    throw new Error('Contenitore non trovato per lo screenshot');
  }
  const prevScroll = (container as HTMLElement).scrollLeft;
  const prevWidth = (container as HTMLElement).style.width;
  (container as HTMLElement).scrollLeft = 0;
  (container as HTMLElement).style.width = `${(container as HTMLElement).scrollWidth}px`;
  const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
  (container as HTMLElement).style.width = prevWidth;
  (container as HTMLElement).scrollLeft = prevScroll;

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth - 24;
  const ratio = canvas.height / canvas.width;
  const imgHeight = imgWidth * ratio;
  const finalWidth = imgWidth;
  const finalHeight = Math.min(imgHeight, pageHeight - 40);
  pdf.addImage(imgData, 'PNG', 12, (pageHeight - finalHeight) / 2, finalWidth, finalHeight, undefined, 'FAST');
  const meseNome = mesi[meseIndex] || '';
  pdf.save(`Calendario_${negozio}_${meseNome}_${anno}_screenshot.pdf`);
}

// Wrapper che sposta fuori dalla componente la logica di export PDF
// Mantiene la stessa configurazione già usata in GenerazioneOrario.tsx
export function esportaPdfMatrice(params: {
  negozio: string;
  meseIndex: number;
  anno: number;
  mesi: string[];
  matrice: OrarioMatriceLike[];
  dipendentiAttivi?: { [nome: string]: boolean };
  periodiMaternita?: Record<string, { start: string; end: string }[]>;
  shouldMaskAssenze: boolean;
  isPrivileged: boolean;
  getDisplayTurnoLabel: (t: any) => string;
}): void {
  exportMatriceTabPdf(params);
}

// Wrapper per lo screenshot PDF che gestisce l'attivazione/disattivazione della modalità screenshot
export async function esportaScreenshotMatrice(params: {
  container: HTMLElement | null;
  negozio: string;
  meseIndex: number;
  anno: number;
  mesi: string[];
  setScreenshotMode: (v: boolean) => void;
  delayMs?: number;
}): Promise<void> {
  const { container, negozio, meseIndex, anno, mesi, setScreenshotMode, delayMs } = params;
  if (!container) throw new Error('Contenitore non valido per lo screenshot');
  setScreenshotMode(true);
  await new Promise(r => setTimeout(r, delayMs ?? 75));
  await exportMatriceScreenshotPdf(container, { negozio, meseIndex, anno, mesi });
  setScreenshotMode(false);
}
